import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postData, fetchData } from "../redux/slices/apiCallSlice";
import { selectApiUrls } from "../redux/slices/apiUrlSlice";

const useCreateBookingItem = () => {
  const [status, setStatus] = useState({});
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);

  const postToApi = async (url, jobId, apiName, logs, apiStatuses, setApiStatus, additionalData = {}) => {
    apiStatuses[apiName] = "loading";
    setApiStatus({ ...apiStatuses });
    logs.push(`Posting to ${apiName} API...`);
    try {
      const response = await dispatch(postData({
        url,
        data: { projectID: String(jobId), ...additionalData },
        contract: { requestCase: 'pascal' },
      })).unwrap();
      setStatus((prevStatus) => ({ ...prevStatus, [apiName]: "success" }));
      logs.push(`Posted to ${apiName} API successfully.`);
      apiStatuses[apiName] = "success";
      return response; // Return the response to get the ID if needed
    } catch (error) {
      logs.push(`Failed to post to ${apiName} API: ${error.message}`);
      apiStatuses[apiName] = "failed";
      return null;
    } finally {
      setApiStatus({ ...apiStatuses });
    }
  };

  const createBookingItem = async (bookingData, setApiStatus) => {
    const logs = [];
    const apiStatuses = {
      generalProject: "loading",
      priceBreakdown: "idle",
      soldToShipTo: "idle",
      bookingJobDetail: "idle",
    };

    try {
      // Prepare data for GeneralProject - only include relevant fields
      const generalProjectData = {
        JobNumber: bookingData.JobNumber,
        ProjectName: bookingData.ProjectName,
        SalesRep: bookingData.SalesRep,
        ProjectManager: bookingData.ProjectManager,
        RequestedDeliveryDate: bookingData.RequestedDeliveryDate,
        OrderDate: bookingData.OrderDate,
        State: bookingData.State,
        // Required boolean fields
        CustomControlsEngineering: 0,
        HasSubmittal: 0,
        NewConstruction: 0,
        SendDocumentsToSales: 0,
        BookingLogOnly: 1,
        // Add JobStatus if provided
        ...(bookingData.JobStatus && { JobStatus: bookingData.JobStatus }),
      };

      // Post to generalProject API first
      logs.push("Posting to generalProject API...");
      const generalProjectCreateResponse = await dispatch(
        postData({
          url: apiUrls.generalProject,
          data: generalProjectData,
          contract: { requestCase: 'pascal' },
        })
      ).unwrap();
      setStatus((prevStatus) => ({ ...prevStatus, generalProject: "success" }));
      logs.push("Posted to generalProject API successfully.");
      apiStatuses.generalProject = "success";
      setApiStatus({ ...apiStatuses });

      let jobId = generalProjectCreateResponse?.ID || generalProjectCreateResponse?.id;

      if (!jobId) {
        logs.push("Fetching generalProject records to resolve the created job ID...");
        const generalProjectResponse = await dispatch(fetchData({ url: apiUrls.generalProject })).unwrap();
        const matchingRecord = generalProjectResponse.find(
          (record) => String(record.JobNumber || record.jobNumber || '').toLowerCase() === String(bookingData.JobNumber || '').toLowerCase()
        );

        if (matchingRecord) {
          jobId = matchingRecord.ID || matchingRecord.id;
        }
      }

      if (!jobId) {
        throw new Error('Failed to resolve the created generalProject ID');
      }

      logs.push(`Resolved created generalProject ID: ${jobId}`);

      // Post to PriceBreakdown with specific fields
      const priceBreakdownData = {
        market: bookingData.Market,
        sellPriceAmount: bookingData.SellPriceAmount ? 
          parseFloat(bookingData.SellPriceAmount.toString().replace(/[$,]/g, '')) || 0 : 0,
      };
      await postToApi(apiUrls.priceBreakdown, jobId, "priceBreakdown", logs, apiStatuses, setApiStatus, priceBreakdownData);
      
      // Post to SoldToShipTo with specific fields
      const soldToShipToData = {
        zone: 1,
        soldToCompanyName: bookingData.SoldToCompanyName,
        soldToState: bookingData.State,
      };
      await postToApi(apiUrls.soldToShipTo, jobId, "soldToShipTo", logs, apiStatuses, setApiStatus, soldToShipToData);
      
      // Post to BookingJobDetail
      await postToApi(apiUrls.bookingJobDetail, jobId, "bookingJobDetail", logs, apiStatuses, setApiStatus);

      return { jobId, logs, apiStatuses };
    } catch (error) {
      console.error("Error creating new booking item:", error);
      setStatus((prevStatus) => ({ ...prevStatus, [error.api]: "failed" }));
      logs.push(`Failed to create new booking item: ${error.message}`);
      apiStatuses[error.api] = "failed";
      setApiStatus({ ...apiStatuses });
      const bookingError = new Error(`Failed to create new booking item: ${error.message}`);
      bookingError.logs = logs;
      bookingError.apiStatuses = apiStatuses;
      bookingError.originalError = error;
      throw bookingError;
    }
  };

  return { createBookingItem, status };
};

export default useCreateBookingItem;
